import React, { useEffect, useState, useRef } from "react";
import { HttpTransportType, HubConnectionBuilder } from "@microsoft/signalr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import CONFIG from "../services/config";
import { fetchTTS } from "../services/ttsService";
import './MessageBoard.css';

function formatTimeAgo(date) {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  return new Date(date).toLocaleString();
}

function isSpeakableText(text) {
  const trimmed = text.trim();
  return /[a-zA-Z0-9ก-๙]/.test(trimmed);
}

function Messageboard() {
  const [groupMessages, setGroupMessages] = useState({});
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState("th-TH-PremwadeeNeural");
  const [autoPlay, setAutoPlay] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const selectedGroupRef = useRef(null);
  const chatEndRef = useRef(null);
  const audioQueueRef = useRef([]);
  const currentAudioIndexRef = useRef(0);
  const lastPlayedTimestampRef = useRef(0);

  const playAudioQueue = () => {
  const queue = audioQueueRef.current;
  const index = currentAudioIndexRef.current;

    if (queue.length === 0 || index >= queue.length || !autoPlay) return;

    const audio = queue[index];
    if (!audio) return;

    audio.currentTime = 0;
    audio.play().catch((e) => console.warn("Audio play error", e));

    audio.onended = () => {
      currentAudioIndexRef.current += 1;
      if (currentAudioIndexRef.current < queue.length) {
        playAudioQueue();
      }
    };
  };

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(`${CONFIG.API_BASE_URL}/chatHub`, {
        withCredentials: true,
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .build();

    try {
        connection.start();
        console.log("✅ Connected to SignalR");
      } catch (err) {
        console.warn("⚠️ Failed to connect to SignalR. Retrying in 1s...");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }


    connection.on("ReceiveMessage", async (data) => {
      const { user, message, groupId, groupName } = data;

      setGroups((prevGroups) => {
        const exists = prevGroups.some((g) => g.id === groupId);
        if (!exists) return [...prevGroups, { id: groupId, name: groupName }];
        return prevGroups;
      });

      if (!selectedGroupRef.current) {
        setSelectedGroup(groupId);
        selectedGroupRef.current = groupId;
      }

      connection.on("GroupJoined", (group) => {
        setGroups((prev) => {
          const exists = prev.some((g) => g.id === group.groupId);
          return exists ? prev : [...prev, group];
        });
      });

      connection.on("GroupLeft", ({ groupId }) => {
        setGroups((prev) => prev.filter((g) => g.id !== groupId));

        if (selectedGroupRef.current === groupId) {
          setSelectedGroup(null);
          selectedGroupRef.current = null;
          setGroupMessages({});
        }
      });

     let audioUrl = null;
     if (groupId === selectedGroupRef.current){
     }
     else{
      selectedGroupRef.current = groupId;
     }

      if (ttsEnabled && groupId === selectedGroupRef.current) {
        audioUrl = await fetchTTS(message, selectedVoice);
      }

      setGroupMessages((prev) => {
        const groupMsgs = prev[groupId] || [];

        const updated=[
          ...groupMsgs,{
            user,message,audioUrl,timestamp: new Date().getTime(), groupId
          },
        ];

        updated.sort((a, b) => a.timestamp - b.timestamp);

        
        return {
          ...prev,
          [groupId]: updated,
        };
      });
    });

    return () => {
      connection.stop();
    };
  }, [ttsEnabled, selectedVoice]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [groupMessages, selectedGroup]);

  useEffect(() => {
    selectedGroupRef.current = selectedGroup;
  }, [selectedGroup]);
  

  const currentMessages = groupMessages[selectedGroup] || [];

  useEffect(() => {
  if (!autoPlay || currentMessages.length === 0) return;

  const unqueued = currentMessages
    .filter(msg => msg.timestamp > lastPlayedTimestampRef.current)
    .map(msg => msg.audioRef)
    .filter(ref => ref instanceof HTMLAudioElement);

  if (unqueued.length === 0) return;

  audioQueueRef.current.push(...unqueued);
  lastPlayedTimestampRef.current = currentMessages[currentMessages.length - 1].timestamp;

  if (currentAudioIndexRef.current >= audioQueueRef.current.length - unqueued.length) {
    playAudioQueue();
  }
}, [currentMessages, autoPlay]);

return (
    <div className="chat-container">
      <div className="chat-header">
        <h2 className="chat-title">Telegram Messages</h2>

        <div className="tts-controls">
          <div className="tts-toggle">
            <span className="tts-label">TTS</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={ttsEnabled}
                onChange={() => setTtsEnabled(!ttsEnabled)}
              />
              <span className="slider"></span>
            </label>
          </div>

          {ttsEnabled && (
            <div className="auto-play-toggle">
              <span className="auto-label">Auto Play</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={autoPlay}
                  onChange={() => {
                    const now = new Date().getTime();
                    setAutoPlay((prev) => {
                      const newValue = !prev;
                      return newValue;
                    });
                  }}
                />
                <span className="slider"></span>
              </label>
            </div>
          )}

          <div className="voice-dropdown">
            <span className="auto-label">Select Voice</span>
            <select
              value={selectedVoice}
              onChange={(e) => setSelectedVoice(e.target.value)}
              className="voice-select"
            >
              <option value="th-TH-PremwadeeNeural">Premwadee</option>
              <option value="th-TH-NiwatNeural">Niwat</option>
              <option value="th-TH-AcharaNeural">Achara</option>
              <option value="en-US-PhoebeMultilingualNeural"> Phobe</option>
              <option value="zh-CN-Xiaochen:DragonHDLatestNeural">Xiaochen</option>
              <option value="en-US-DerekMultilingualNeural">Derek</option>
            </select>
          </div>
          <button
            className="preview-btn"
            onClick={() =>
              fetchTTS("สวัสดีค่ะ", selectedVoice).then((url) => {
                const preview = new Audio(url);
                preview.play();
              })
            }
          >
            🔊 Preview Voice
          </button>

          {groups.length > 0 && (
            <div className="group-dropdown">
              <span className="auto-label">Select Group</span>
              <select
                value={selectedGroup || ""}
                onChange={(e) => {
                  const newGroupId = e.target.value;
                  setSelectedGroup(newGroupId);
                  selectedGroupRef.current = newGroupId;
                  setGroupMessages((prev) => ({...prev,[newGroupId]: [],}));
                  audioQueueRef.current = [];
                  currentAudioIndexRef.current = 0;
                  lastPlayedTimestampRef.current = 0;
                }}
                className="voice-select"
              >
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="chat-box">
        {currentMessages.map((msg, i) => {
          return (
            <div className="chat-message" key={i}>
              <div className="chat-message-content">
                <div>
                  <div className="chat-user">{msg.user}</div>
                  <div className="chat-text">{msg.message}</div>

                  {msg.audioUrl && (
                    <div className="audio-wrapper">
                      {autoPlay ? (
                        <>
                          <audio
                            ref={(el) => (msg.audioRef = el)}
                            src={msg.audioUrl}
                            controls={!autoPlay}
                            className="chat-audio"
                          />
                          {isSpeakableText(msg.message) && (
                            <button
                              className="replay-button"
                              onClick={() => {
                                if (msg.audioRef) {
                                  msg.audioRef.currentTime = 0;
                                  msg.audioRef.play();
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faRotateRight} />
                            </button>
                          )}
                        </>
                      ) : (
                        <audio
                          ref={(el) => (msg.audioRef = el)}
                          src={msg.audioUrl}
                          controls
                          className="chat-audio"
                        />
                      )}
                    </div>
                  )}
                </div>
                <div className="message-timestamp">
                  {formatTimeAgo(msg.timestamp)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>
    </div>
  );
}

export default Messageboard;